import { useMemo, useState, type ReactNode } from 'react';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Skeleton,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography,
  Upload,
  type TableColumnsType,
} from 'antd';
import { BarChartOutlined, EditOutlined, InboxOutlined, PlusOutlined, UndoOutlined, UploadOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { errMsg } from '../../lib/errors';
import { QueryError } from '../../lib/QueryState';
import { PAISE_PER_RUPEE } from '../../lib/money';
import { INACTIVE_PACKAGE_SUFFIX, packagesForPicker } from '../../lib/packageOptions';
import { oneOf, useUrlState } from '../../lib/urlState';
import { Can } from '../../rbac/RequirePermission';
import { usePermissions } from '../../auth/store';
import { formatWhen } from '../listeners/listenerStats';
import { PromoPreview, type PreviewPkg } from './PromoPreview';
import {
  BONUS_TYPE_LABELS,
  CTA_MAX,
  emptyPromoForm,
  formatCountdown,
  formFromCampaign,
  isMoneyType,
  loginOfferPreset,
  MESSAGE_MAX,
  NAME_MAX,
  POLICY_LABELS,
  PRIORITY_MAX,
  PRIORITY_MIN,
  promoBody,
  promoFormError,
  promoImageProblem,
  promoPriceForFixedOff,
  promoPriceForPercent,
  PROMO_IMAGE_TYPES,
  scheduleState,
  SUBTITLE_MAX,
  TITLE_MAX,
  TRIGGER_LABELS,
  TYPE_LABELS,
  type AdminPromoCampaign,
  type PromoDesignForm,
  type PromoForm,
  type PromoStats,
  type PromoTrigger,
  type PromoType,
} from './promotions';

const LIST_KEY = ['admin', 'promos'];

/** Minimal shape of `GET /admin/coin-packages` rows used here. */
interface CoinPkg {
  id: string;
  name: string;
  coins: number;
  bonusCoins: number;
  /** Paise, as the API's string — used only to mirror the server's % OFF. */
  amountPaise: string;
  priceLabel: string;
  isActive: boolean;
  displayOrder: number;
}

const TAB_KEYS = ['offers', 'bonus', 'warnings', 'safety'] as const;
type TabKey = (typeof TAB_KEYS)[number];
const TABS: Array<{ key: TabKey; type: PromoType; label: string }> = [
  { key: 'offers', type: 'recharge_offer', label: 'Recharge offers' },
  { key: 'bonus', type: 'bonus_popup', label: 'Login & bonus popups' },
  { key: 'warnings', type: 'call_warning', label: 'Call warnings' },
  { key: 'safety', type: 'safety_banner', label: 'Safety banners' },
];

function serverError(e: unknown): { message: string; fields: Record<string, string> } {
  const data = (e as { response?: { data?: { message?: string; fields?: Record<string, string> } } })
    .response?.data;
  return { message: data?.message ?? 'Could not save the campaign', fields: data?.fields ?? {} };
}

/**
 * Promotions & messaging — one campaign list per kind (recharge offers,
 * login/bonus popups, call warnings, safety banners), each created and edited
 * in a modal with a live phone-style preview. The API is the source of truth
 * for every rule; this page mirrors the validation so OK stays disabled while
 * the form cannot be saved.
 */
export function PromotionsPage() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { can } = usePermissions();
  const { get, set } = useUrlState();
  const tab: TabKey = oneOf(get('tab'), TAB_KEYS, 'offers') ?? 'offers';
  const showArchived = get('archived') === '1';
  const tabType = TABS.find((t) => t.key === tab)?.type ?? 'recharge_offer';

  /** null = closed, 'new' = creating, otherwise the campaign being edited. */
  const [editing, setEditing] = useState<AdminPromoCampaign | 'new' | null>(null);
  const [form, setForm] = useState<PromoForm>(emptyPromoForm('recharge_offer'));
  const [serverFields, setServerFields] = useState<Record<string, string>>({});
  const [statsFor, setStatsFor] = useState<AdminPromoCampaign | null>(null);

  const list = useQuery({
    queryKey: [...LIST_KEY, 'list', showArchived],
    queryFn: async () =>
      (
        await api.get<{ campaigns: AdminPromoCampaign[] }>(
          showArchived ? '/admin/promos?archived=true' : '/admin/promos',
        )
      ).data.campaigns,
  });
  const packages = useQuery({
    queryKey: ['admin', 'coin-packages'],
    queryFn: async () => (await api.get<{ packages: CoinPkg[] }>('/admin/coin-packages')).data.packages,
  });
  // Active packages in store order; an already-chosen inactive one stays
  // listed (flagged) so the form never shows a bare id.
  const pkgOptions = useMemo(
    () =>
      packagesForPicker(packages.data ?? [], [form.packageId, ...(form.eligiblePackageIds ?? [])]).map((p) => ({
        value: p.id,
        label: `${p.name} · ${p.coins} coins · ${p.priceLabel}${p.isActive ? '' : INACTIVE_PACKAGE_SUFFIX}`,
      })),
    [packages.data, form.packageId, form.eligiblePackageIds],
  );
  const rows = useMemo(
    () => (list.data ?? []).filter((c) => c.type === tabType && (showArchived || c.archivedAt === null)),
    [list.data, tabType, showArchived],
  );

  const refresh = () => qc.invalidateQueries({ queryKey: LIST_KEY });

  const patchForm = (patch: Partial<PromoForm>) => {
    setForm((f) => ({ ...f, ...patch }));
    if (Object.keys(serverFields).length > 0) setServerFields({});
  };

  /** Design lives in its own nested object, so it gets its own patcher. */
  const patchDesign = (patch: Partial<PromoDesignForm>) => {
    setForm((f) => ({ ...f, design: { ...f.design, ...patch } }));
    if (Object.keys(serverFields).length > 0) setServerFields({});
  };

  const openCreate = () => {
    setForm(emptyPromoForm(tabType));
    setServerFields({});
    setEditing('new');
  };
  /** Prefilled "one-week login recharge discount": login trigger + a per-user
   *  7-day window + badge copy. The admin still picks the package and price. */
  const openLoginOffer = () => {
    setForm(loginOfferPreset());
    setServerFields({});
    setEditing('new');
  };
  const openEdit = (c: AdminPromoCampaign) => {
    setForm(formFromCampaign(c));
    setServerFields({});
    setEditing(c);
  };

  const save = useMutation({
    mutationFn: () =>
      editing === 'new'
        ? api.post('/admin/promos', promoBody(form))
        : api.patch(`/admin/promos/${(editing as AdminPromoCampaign).id}`, promoBody(form)),
    onSuccess: () => {
      message.success(editing === 'new' ? 'Campaign created' : 'Campaign updated');
      setEditing(null);
      setServerFields({});
      void refresh();
    },
    onError: (e) => {
      const err = serverError(e);
      setServerFields(err.fields);
      message.error(err.message);
    },
  });

  const toggle = useMutation({
    mutationFn: ({ id, enable }: { id: string; enable: boolean }) =>
      api.post(`/admin/promos/${id}/${enable ? 'activate' : 'deactivate'}`),
    onSuccess: (_, v) => {
      message.success(v.enable ? 'Campaign is ON' : 'Campaign is OFF');
      void refresh();
    },
    onError: (e) => message.error(errMsg(e, 'Could not change the status')),
  });

  const archive = useMutation({
    mutationFn: ({ id, restore }: { id: string; restore: boolean }) =>
      api.post(`/admin/promos/${id}/${restore ? 'unarchive' : 'archive'}`),
    onSuccess: (_, v) => {
      message.success(v.restore ? 'Campaign restored (left OFF)' : 'Campaign archived');
      void refresh();
    },
    onError: (e) => message.error(errMsg(e, 'Could not archive the campaign')),
  });

  /** Browser → storage on a short-lived URL; saved on the campaign as `imageKey`. */
  const uploadImage = useMutation({
    mutationFn: async (file: File) => {
      const bad = promoImageProblem(file);
      if (bad) throw new Error(bad);
      const { data: minted } = await api.post<{ key: string; url: string }>(
        '/admin/promos/image/upload-url',
        { contentType: file.type, sizeBytes: file.size },
      );
      const res = await fetch(minted.url, {
        method: 'PUT',
        headers: { 'content-type': file.type },
        body: file,
      });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      return { key: minted.key, preview: URL.createObjectURL(file) };
    },
    onSuccess: ({ key, preview }) => patchForm({ imageKey: key, imageUrl: preview }),
    onError: (e) =>
      message.error(e instanceof Error ? e.message : errMsg(e, 'Could not upload the image')),
  });

  const columns: TableColumnsType<AdminPromoCampaign> = [
    {
      title: 'Campaign',
      render: (_: unknown, c) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 600 }}>{c.name}</span>
          <Typography.Text type="secondary" style={{ fontSize: 12 }} ellipsis={{ tooltip: c.title }}>
            {c.title}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      width: 180,
      render: (_: unknown, c) => {
        const when = scheduleState(c);
        return (
          <Space size={4} wrap>
            {c.archivedAt !== null ? (
              <Tag>Archived</Tag>
            ) : (
              <Tag color={c.enabled ? 'green' : 'default'}>{c.enabled ? 'Enabled' : 'Disabled'}</Tag>
            )}
            {when === 'scheduled' && <Tag color="blue">Scheduled</Tag>}
            {when === 'expired' && <Tag color="orange">Expired</Tag>}
          </Space>
        );
      },
    },
    { title: 'Trigger', width: 160, render: (_: unknown, c) => TRIGGER_LABELS[c.trigger] },
    { title: 'Priority', width: 90, align: 'right', dataIndex: 'priority' },
    {
      title: 'Schedule',
      width: 220,
      render: (_: unknown, c) =>
        c.startsAt === null && c.endsAt === null ? (
          <Typography.Text type="secondary">Always</Typography.Text>
        ) : (
          <Typography.Text style={{ fontSize: 12 }}>
            {c.startsAt ? formatWhen(c.startsAt) : '—'} → {c.endsAt ? formatWhen(c.endsAt) : '—'}
          </Typography.Text>
        ),
    },
    {
      title: 'Stats',
      width: 70,
      render: (_: unknown, c) => (
        <Button
          size="small"
          icon={<BarChartOutlined />}
          aria-label={`Stats for ${c.name}`}
          onClick={() => setStatsFor(c)}
        />
      ),
    },
    {
      title: 'Actions',
      width: 230,
      render: (_: unknown, c) => {
        const archived = c.archivedAt !== null;
        // Turning a money campaign ON changes what users pay/receive — confirm it.
        const confirmEnable = isMoneyType(c.type) && !c.enabled && !archived;
        return (
          <Space size={4} wrap>
            <Can permission="promos.edit">
              <Button size="small" icon={<EditOutlined />} disabled={archived} onClick={() => openEdit(c)}>
                Edit
              </Button>
            </Can>
            <Can permission="promos.toggle">
              <Popconfirm
                title="Turn this campaign on?"
                description="It changes what users are offered or charged. Users matching its rules will start seeing it."
                okText="Turn on"
                disabled={!confirmEnable}
                onConfirm={() => toggle.mutate({ id: c.id, enable: true })}
              >
                <Switch
                  size="small"
                  aria-label={`${c.enabled ? 'Disable' : 'Enable'} ${c.name}`}
                  checked={c.enabled}
                  loading={toggle.isPending && toggle.variables?.id === c.id}
                  disabled={archived || toggle.isPending || !can('promos.toggle')}
                  onChange={(v) => {
                    if (confirmEnable) return; // the Popconfirm carries this one
                    toggle.mutate({ id: c.id, enable: v });
                  }}
                />
              </Popconfirm>
            </Can>
            <Can permission="promos.archive">
              {archived ? (
                <Popconfirm
                  title="Restore this campaign?"
                  description="It comes back DISABLED — turn it on separately."
                  okText="Restore"
                  onConfirm={() => archive.mutate({ id: c.id, restore: true })}
                >
                  <Button
                    size="small"
                    icon={<UndoOutlined />}
                    loading={archive.isPending && archive.variables?.id === c.id}
                  >
                    Unarchive
                  </Button>
                </Popconfirm>
              ) : (
                <Popconfirm
                  title="Archive this campaign?"
                  description="It is switched off and hidden from the list (kept for the record)."
                  okText="Archive"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => archive.mutate({ id: c.id, restore: false })}
                >
                  <Button
                    size="small"
                    danger
                    icon={<InboxOutlined />}
                    aria-label={`Archive ${c.name}`}
                    loading={archive.isPending && archive.variables?.id === c.id}
                  />
                </Popconfirm>
              )}
            </Can>
          </Space>
        );
      },
    },
  ];

  const problem = promoFormError(form);
  const money = isMoneyType(form.type);
  const selectedPkg: CoinPkg | null = useMemo(
    () => (packages.data ?? []).find((x) => x.id === form.packageId) ?? null,
    [packages.data, form.packageId],
  );
  const previewPkg: PreviewPkg | null = useMemo(
    () =>
      selectedPkg
        ? {
            name: selectedPkg.name,
            priceLabel: selectedPkg.priceLabel,
            coins: selectedPkg.coins,
            amountPaise: selectedPkg.amountPaise,
          }
        : null,
    [selectedPkg],
  );

  const field = (label: string, node: ReactNode, key?: string) => (
    <div>
      <Typography.Text strong>{label}</Typography.Text>
      {node}
      {key !== undefined && serverFields[key] !== undefined && (
        <Typography.Text type="danger" style={{ display: 'block', fontSize: 12 }}>
          {serverFields[key]}
        </Typography.Text>
      )}
    </div>
  );

  const tabTable = (
    <Card styles={{ body: { padding: 0 } }}>
      {list.isError ? (
        <QueryError
          error={list.error}
          what="promotion campaigns"
          onRetry={() => void list.refetch()}
          style={{ margin: 16 }}
        />
      ) : (
        <Table
          rowKey="id"
          loading={list.isLoading}
          dataSource={rows}
          columns={columns}
          pagination={false}
          scroll={{ x: 980 }}
          locale={{ emptyText: `No ${TYPE_LABELS[tabType].toLowerCase()} campaigns yet.` }}
        />
      )}
    </Card>
  );

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <Space direction="vertical" size={0}>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Promotions &amp; messaging
          </Typography.Title>
          <Typography.Text type="secondary">
            Recharge offers, bonus popups, call warnings and safety banners shown in the app.
          </Typography.Text>
        </Space>
        <Space>
          <Space size={6}>
            <Switch
              size="small"
              aria-label="Show archived"
              checked={showArchived}
              onChange={(v) => set({ archived: v ? '1' : null })}
            />
            <Typography.Text type="secondary">Show archived</Typography.Text>
          </Space>
          <Can permission="promos.create">
            <Space>
              {tab === 'offers' && (
                <Button onClick={openLoginOffer}>7-day login offer</Button>
              )}
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                New campaign
              </Button>
            </Space>
          </Can>
        </Space>
      </Space>

      <Tabs
        activeKey={tab}
        onChange={(k) => set({ tab: k === 'offers' ? null : k })}
        items={TABS.map((t) => ({ key: t.key, label: t.label, children: tabTable }))}
      />

      <Modal
        title={
          editing === 'new'
            ? `New ${TYPE_LABELS[form.type].toLowerCase()}`
            : `Edit ${TYPE_LABELS[form.type].toLowerCase()}`
        }
        open={editing !== null}
        width={760}
        onCancel={() => setEditing(null)}
        onOk={() => save.mutate()}
        okText={editing === 'new' ? 'Create' : 'Save'}
        okButtonProps={{ disabled: problem !== null || uploadImage.isPending, loading: save.isPending }}
        destroyOnClose
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} md={13}>
            <Space direction="vertical" style={{ width: '100%' }} size={12}>
              {field(
                'Name (internal)',
                <Input
                  aria-label="Campaign name"
                  maxLength={NAME_MAX}
                  placeholder="e.g. Diwali ₹49 pack"
                  value={form.name}
                  onChange={(e) => patchForm({ name: e.target.value })}
                />,
                'name',
              )}
              {field(
                'Title (optional)',
                <Input
                  aria-label="Campaign title"
                  maxLength={TITLE_MAX}
                  value={form.title}
                  onChange={(e) => patchForm({ title: e.target.value })}
                />,
                'title',
              )}
              {field(
                'Message (optional)',
                <Input.TextArea
                  aria-label="Campaign message"
                  rows={3}
                  maxLength={MESSAGE_MAX}
                  showCount
                  value={form.message}
                  onChange={(e) => patchForm({ message: e.target.value })}
                />,
                'message',
              )}
              {field(
                'Subtitle (optional)',
                <Input
                  aria-label="Campaign subtitle"
                  maxLength={SUBTITLE_MAX}
                  value={form.subtitle}
                  onChange={(e) => patchForm({ subtitle: e.target.value })}
                />,
                'subtitle',
              )}
              <Space wrap>
                {field(
                  'Button text',
                  <Input
                    aria-label="CTA text"
                    maxLength={CTA_MAX}
                    style={{ width: 150 }}
                    placeholder={form.type === 'call_warning' ? 'I Agree' : 'Recharge now'}
                    value={form.ctaText}
                    onChange={(e) => patchForm({ ctaText: e.target.value })}
                  />,
                  'ctaText',
                )}
                {field(
                  'Dismiss text',
                  <Input
                    aria-label="Dismiss text"
                    maxLength={CTA_MAX}
                    style={{ width: 150 }}
                    placeholder="Not now"
                    disabled={form.requireAck}
                    value={form.dismissText}
                    onChange={(e) => patchForm({ dismissText: e.target.value })}
                  />,
                  'dismissText',
                )}
              </Space>

              {field(
                'Image (optional)',
                <div>
                  <Space>
                    {form.imageUrl && (
                      <img
                        src={form.imageUrl}
                        alt=""
                        style={{ width: 40, height: 40, borderRadius: 8, objectFit: 'cover' }}
                      />
                    )}
                    <Upload
                      accept={PROMO_IMAGE_TYPES.join(',')}
                      showUploadList={false}
                      beforeUpload={(file) => {
                        uploadImage.mutate(file);
                        return false;
                      }}
                    >
                      <Button icon={<UploadOutlined />} loading={uploadImage.isPending}>
                        {form.imageKey ? 'Replace' : 'Upload'}
                      </Button>
                    </Upload>
                    {form.imageKey && (
                      <Button type="link" danger onClick={() => patchForm({ imageKey: null, imageUrl: null })}>
                        Remove
                      </Button>
                    )}
                  </Space>
                  {form.imageUrl && (
                    <div style={{ marginTop: 10 }}>
                      {/* Layout of the text drawn OVER the banner. Only shown
                          with an image, because that is the only time it does
                          anything — an image-only campaign simply leaves the
                          text fields empty. */}
                      <Space wrap size="middle">
                        <span>
                          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            Text position
                          </Typography.Text>
                          <br />
                          <Select
                            data-testid="banner-text-pos"
                            style={{ width: 130 }}
                            value={form.bannerTextPos}
                            onChange={(v) => patchForm({ bannerTextPos: v })}
                            options={[
                              { value: 'top', label: 'Top' },
                              { value: 'center', label: 'Center' },
                              { value: 'bottom', label: 'Bottom' },
                            ]}
                          />
                        </span>
                        <span>
                          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            Text align
                          </Typography.Text>
                          <br />
                          <Select
                            data-testid="banner-text-align"
                            style={{ width: 130 }}
                            value={form.bannerTextAlign}
                            onChange={(v) => patchForm({ bannerTextAlign: v })}
                            options={[
                              { value: 'left', label: 'Left' },
                              { value: 'center', label: 'Center' },
                              { value: 'right', label: 'Right' },
                            ]}
                          />
                        </span>
                        <span>
                          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                            Readability shade
                          </Typography.Text>
                          <br />
                          <Switch
                            data-testid="banner-overlay"
                            checked={form.bannerOverlay}
                            onChange={(v) => patchForm({ bannerOverlay: v })}
                          />
                        </span>
                      </Space>
                      <div>
                        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                          The image fills the popup; title, message and the button sit on top of it.
                          Leave the text fields empty for an image-only campaign.
                        </Typography.Text>
                      </div>
                    </div>
                  )}
                </div>,
                'imageKey',
              )}

              <Space wrap>
                {(['bgColor', 'textColor', 'accentColor', 'buttonColor'] as const).map((k) => (
                  <span key={k}>
                    {field(
                      { bgColor: 'Background', textColor: 'Text', accentColor: 'Accent', buttonColor: 'Button' }[k],
                      <Input
                        aria-label={`${k} hex`}
                        placeholder="#RRGGBB"
                        style={{ width: 110 }}
                        maxLength={7}
                        value={form[k]}
                        onChange={(e) => patchForm({ [k]: e.target.value })}
                      />,
                      k,
                    )}
                  </span>
                ))}
              </Space>

              <Card size="small" title="What the popup shows">
                <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
                  The popup draws what you type here and nothing else. The price, the
                  struck-through &ldquo;was&rdquo; price, the pack name and the placeholder
                  artwork are generated — switch them on only if you want them. A button
                  with no text is not drawn at all.
                </Typography.Paragraph>

                <Space wrap size={[24, 12]}>
                  {(
                    [
                      ['showTitle', 'Title'],
                      ['showMessage', 'Message'],
                      ['showSubtitle', 'Subtitle'],
                      ['showPackage', 'Price / pack details'],
                      ['showArt', 'Placeholder artwork'],
                      ['showCta', 'Action button'],
                      ['showDismiss', 'Dismiss link'],
                    ] as const
                  ).map(([k, label]) => (
                    <Space key={k} size={8}>
                      <Switch
                        size="small"
                        aria-label={label}
                        checked={form.design[k]}
                        onChange={(v) => patchDesign({ [k]: v })}
                      />
                      <Typography.Text style={{ fontSize: 13 }}>{label}</Typography.Text>
                    </Space>
                  ))}
                </Space>

                <div style={{ marginTop: 16 }}>
                  <Typography.Text strong style={{ fontSize: 13 }}>
                    Text colours
                  </Typography.Text>
                  <Space wrap style={{ marginTop: 8 }}>
                    {(
                      [
                        ['titleColor', 'Title'],
                        ['messageColor', 'Message'],
                        ['subtitleColor', 'Subtitle'],
                        ['priceColor', 'Price'],
                        ['ctaTextColor', 'Button text'],
                        ['dismissTextColor', 'Dismiss text'],
                      ] as const
                    ).map(([k, label]) => (
                      <span key={k}>
                        {field(
                          label,
                          <Input
                            aria-label={`${k} hex`}
                            placeholder="#RRGGBB"
                            style={{ width: 110 }}
                            maxLength={7}
                            value={form.design[k]}
                            onChange={(e) => patchDesign({ [k]: e.target.value })}
                          />,
                          `design.${k}`,
                        )}
                      </span>
                    ))}
                  </Space>
                  <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                    Leave empty to inherit the Text colour above (Price inherits Accent).
                  </Typography.Text>
                </div>

                <div style={{ marginTop: 16 }}>
                  <Typography.Text strong style={{ fontSize: 13 }}>
                    Image size
                  </Typography.Text>
                  <Space wrap style={{ marginTop: 8 }}>
                    {field(
                      'Height (px)',
                      <InputNumber
                        aria-label="Image height"
                        min={40}
                        max={520}
                        style={{ width: 120 }}
                        placeholder="auto"
                        value={form.design.imageHeight}
                        onChange={(v) => patchDesign({ imageHeight: v ?? null })}
                      />,
                      'design.imageHeight',
                    )}
                    {field(
                      'Width (% of card)',
                      <InputNumber
                        aria-label="Image width percent"
                        min={20}
                        max={100}
                        style={{ width: 140 }}
                        placeholder="auto"
                        value={form.design.imageWidthPercent}
                        onChange={(v) => patchDesign({ imageWidthPercent: v ?? null })}
                      />,
                      'design.imageWidthPercent',
                    )}
                    {field(
                      'Fit',
                      <Select<'contain' | 'cover'>
                        aria-label="Image fit"
                        style={{ width: 140 }}
                        value={form.design.imageFit}
                        onChange={(v) => patchDesign({ imageFit: v })}
                        options={[
                          { value: 'contain', label: 'Contain (whole image)' },
                          { value: 'cover', label: 'Cover (fill, crop)' },
                        ]}
                      />,
                      'design.imageFit',
                    )}
                  </Space>
                </div>
              </Card>

              <Space wrap>
                {field(
                  'Trigger',
                  <Select<PromoTrigger>
                    aria-label="Trigger"
                    style={{ width: 220 }}
                    value={form.trigger}
                    onChange={(v) => patchForm({ trigger: v })}
                    options={(form.type === 'call_warning'
                      ? (['before_call', 'call_connected'] as const)
                      : // Offers can also run in the sequential low-balance
                        // popup run; safety banners keep the session triggers.
                        money
                        ? (['login', 'home_open', 'app_resume', 'low_balance'] as const)
                        : (['login', 'home_open', 'app_resume'] as const)
                    ).map((t) => ({
                      value: t,
                      label: TRIGGER_LABELS[t],
                    }))}
                  />,
                  'trigger',
                )}
                {field(
                  'Priority',
                  <InputNumber
                    aria-label="Priority"
                    min={PRIORITY_MIN}
                    max={PRIORITY_MAX}
                    style={{ width: 120 }}
                    value={form.priority}
                    onChange={(v) => patchForm({ priority: v ?? 0 })}
                  />,
                  'priority',
                )}
              </Space>

              <Space wrap>
                {field(
                  'Starts',
                  <Input
                    aria-label="Starts at"
                    type="datetime-local"
                    style={{ width: 210 }}
                    value={form.startsAt}
                    onChange={(e) => patchForm({ startsAt: e.target.value })}
                  />,
                  'startsAt',
                )}
                {field(
                  'Ends',
                  <Input
                    aria-label="Ends at"
                    type="datetime-local"
                    style={{ width: 210 }}
                    value={form.endsAt}
                    onChange={(e) => patchForm({ endsAt: e.target.value })}
                  />,
                  'endsAt',
                )}
              </Space>

              {money && (
                <>
                  {form.type === 'recharge_offer' && (
                    <>
                      {field(
                        'Coin package (required)',
                        <Select
                          aria-label="Coin package"
                          showSearch
                          optionFilterProp="label"
                          allowClear
                          style={{ width: '100%' }}
                          placeholder="Pick the package on offer"
                          loading={packages.isLoading}
                          value={form.packageId ?? undefined}
                          onChange={(v: string | undefined) => patchForm({ packageId: v ?? null })}
                          options={pkgOptions}
                        />,
                        'packageId',
                      )}
                      <Space wrap>
                        {field(
                          'Promo price (₹)',
                          <Space>
                            <InputNumber
                              aria-label="Promo price in rupees"
                              min={0}
                              precision={2}
                              style={{ width: 140 }}
                              placeholder="Below list price"
                              value={form.promoAmountRupees}
                              onChange={(v) => patchForm({ promoAmountRupees: v })}
                            />
                            {/* Convenience only: % fills the ₹ field from the
                                package's REAL price; the server still validates
                                the stored price against the package. */}
                            <InputNumber
                              aria-label="Discount percent helper"
                              min={1}
                              max={99}
                              style={{ width: 110 }}
                              placeholder="% off"
                              disabled={selectedPkg == null}
                              value={null}
                              onChange={(pct) => {
                                if (pct == null || selectedPkg == null) return;
                                const rupees = promoPriceForPercent(selectedPkg.amountPaise, pct);
                                if (rupees != null) patchForm({ promoAmountRupees: rupees });
                              }}
                            />
                            <InputNumber
                              aria-label="Fixed discount helper"
                              min={1}
                              style={{ width: 120 }}
                              placeholder="₹ off"
                              disabled={selectedPkg == null}
                              value={null}
                              onChange={(off) => {
                                if (off == null || selectedPkg == null) return;
                                const rupees = promoPriceForFixedOff(selectedPkg.amountPaise, off);
                                if (rupees != null) patchForm({ promoAmountRupees: rupees });
                              }}
                            />
                            {selectedPkg != null && (
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                list {selectedPkg.priceLabel}
                              </Typography.Text>
                            )}
                          </Space>,
                          'promoAmountPaise',
                        )}
                        {field(
                          'Offer validity (days per user)',
                          <Space wrap>
                            <InputNumber
                              aria-label="Offer validity days"
                              min={1}
                              max={30}
                              style={{ width: 110 }}
                              placeholder="7"
                              value={
                                form.countdownSec != null && form.countdownSec % 86_400 === 0
                                  ? form.countdownSec / 86_400
                                  : undefined
                              }
                              onChange={(d) =>
                                patchForm({ countdownSec: d == null ? null : d * 86_400 })
                              }
                            />
                            <Button size="small" onClick={() => patchForm({ countdownSec: 7 * 86_400 })}>
                              7 days
                            </Button>
                            <InputNumber
                              aria-label="Countdown seconds"
                              min={0}
                              style={{ width: 140 }}
                              placeholder="seconds"
                              value={form.countdownSec}
                              onChange={(v) => patchForm({ countdownSec: v })}
                            />
                            {form.countdownSec != null && form.countdownSec > 0 && (
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                ≈ {formatCountdown(form.countdownSec)} per user, from first eligible
                                login — repeat logins never restart it
                              </Typography.Text>
                            )}
                          </Space>,
                          'countdownSec',
                        )}
                        {field(
                          'Countdown display',
                          <Space size={6}>
                            <Switch
                              size="small"
                              aria-label="Show countdown to users"
                              checked={form.showCountdown}
                              onChange={(v) => patchForm({ showCountdown: v })}
                            />
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              {form.showCountdown
                                ? 'Users see the ticking timer'
                                : 'OFF — no timer shown; the window is still enforced silently'}
                            </Typography.Text>
                          </Space>,
                          'showCountdown',
                        )}
                      </Space>
                      <Space wrap>
                        {field(
                          'Display',
                          <Select<'popup' | 'wallet'>
                            aria-label="Offer display"
                            style={{ width: 340 }}
                            value={form.placement === 'wallet' ? 'wallet' : 'popup'}
                            onChange={(v) => patchForm({ placement: v })}
                            options={[
                              {
                                value: 'wallet',
                                label: 'Wallet highlight only — inside Recharge Packages, no popup',
                              },
                              { value: 'popup', label: 'Popup + wallet highlight' },
                            ]}
                          />,
                          'placement',
                        )}
                      </Space>
                    </>
                  )}
                  <Space wrap>
                    {field(
                      'Bonus type',
                      <Select
                        aria-label="Bonus type"
                        allowClear
                        style={{ width: 170 }}
                        placeholder="No bonus"
                        value={form.bonusType ?? undefined}
                        onChange={(v: 'percent' | 'fixed' | undefined) =>
                          patchForm({ bonusType: v ?? null, bonusValue: v === undefined ? null : form.bonusValue })
                        }
                        options={(['percent', 'fixed'] as const).map((b) => ({
                          value: b,
                          label: BONUS_TYPE_LABELS[b],
                        }))}
                      />,
                      'bonusType',
                    )}
                    {field(
                      form.bonusType === 'percent' ? 'Bonus (%)' : 'Bonus (coins)',
                      <InputNumber
                        aria-label="Bonus value"
                        min={0}
                        style={{ width: 120 }}
                        value={form.bonusValue}
                        onChange={(v) => patchForm({ bonusValue: v })}
                      />,
                      'bonusValue',
                    )}
                    {field(
                      'Max bonus coins',
                      <InputNumber
                        aria-label="Max bonus coins"
                        min={0}
                        style={{ width: 130 }}
                        placeholder="No cap"
                        value={form.maxBonusCoins}
                        onChange={(v) => patchForm({ maxBonusCoins: v })}
                      />,
                      'maxBonusCoins',
                    )}
                  </Space>
                  <Space wrap>
                    {field(
                      'Min recharge (₹)',
                      <InputNumber
                        aria-label="Minimum recharge in rupees"
                        min={0}
                        precision={2}
                        style={{ width: 140 }}
                        placeholder="Any"
                        value={form.minRechargeRupees}
                        onChange={(v) => patchForm({ minRechargeRupees: v })}
                      />,
                      'minRechargePaise',
                    )}
                    {field(
                      'Low-balance below (coins)',
                      <InputNumber
                        aria-label="Low balance threshold coins"
                        min={0}
                        style={{ width: 150 }}
                        placeholder="Everyone"
                        value={form.lowBalanceThresholdCoins}
                        onChange={(v) => patchForm({ lowBalanceThresholdCoins: v })}
                      />,
                      'lowBalanceThresholdCoins',
                    )}
                  </Space>
                  {form.type === 'bonus_popup' &&
                    field(
                      'Eligible packages (empty = all)',
                      <Select
                        aria-label="Eligible packages"
                        mode="multiple"
                        allowClear
                        optionFilterProp="label"
                        style={{ width: '100%' }}
                        placeholder="All packages"
                        loading={packages.isLoading}
                        value={form.eligiblePackageIds}
                        onChange={(v: string[]) => patchForm({ eligiblePackageIds: v })}
                        options={pkgOptions}
                      />,
                      'eligiblePackageIds',
                    )}
                </>
              )}

              <Space wrap>
                {field(
                  'Stop showing',
                  <Select
                    aria-label="Completion policy"
                    style={{ width: 230 }}
                    value={form.completionPolicy}
                    onChange={(v) => patchForm({ completionPolicy: v })}
                    options={(Object.keys(POLICY_LABELS) as Array<keyof typeof POLICY_LABELS>).map((p) => ({
                      value: p,
                      label: POLICY_LABELS[p],
                    }))}
                  />,
                  'completionPolicy',
                )}
                {money && (
                  <Space size={6}>
                    <Switch
                      size="small"
                      aria-label="Stop after any recharge"
                      checked={form.stopAfterAnyRecharge}
                      onChange={(v) => patchForm({ stopAfterAnyRecharge: v })}
                    />
                    <Typography.Text>Stop after any recharge</Typography.Text>
                  </Space>
                )}
              </Space>

              <Space wrap>
                {form.placement === 'popup' && (
                  <Space size={6}>
                    <Switch
                      size="small"
                      aria-label="Require acknowledgement"
                      checked={form.requireAck}
                      // requireAck ⇔ a popup that cannot be dismissed (server rule).
                      onChange={(v) => patchForm({ requireAck: v, allowDismiss: !v, showClose: v ? false : form.showClose })}
                    />
                    <Typography.Text>Must be acknowledged (cannot dismiss)</Typography.Text>
                  </Space>
                )}
                <Space size={6}>
                  <Switch
                    size="small"
                    aria-label="Show close button"
                    checked={form.showClose}
                    disabled={form.requireAck}
                    onChange={(v) => patchForm({ showClose: v })}
                  />
                  <Typography.Text>Show a close (✕) button</Typography.Text>
                </Space>
              </Space>

              <Space wrap>
                <Space size={6}>
                  <Switch
                    size="small"
                    aria-label="Show once per user"
                    checked={form.showOncePerUser}
                    onChange={(v) => patchForm({ showOncePerUser: v })}
                  />
                  <Typography.Text>Show once per user</Typography.Text>
                </Space>
                {field(
                  'Max / user',
                  <InputNumber
                    aria-label="Max impressions per user"
                    min={0}
                    style={{ width: 100 }}
                    placeholder="∞"
                    value={form.maxImpressionsPerUser}
                    onChange={(v) => patchForm({ maxImpressionsPerUser: v })}
                  />,
                  'maxImpressionsPerUser',
                )}
                {field(
                  'Max / day',
                  <InputNumber
                    aria-label="Max per day"
                    min={0}
                    style={{ width: 100 }}
                    placeholder="∞"
                    value={form.maxPerDay}
                    onChange={(v) => patchForm({ maxPerDay: v })}
                  />,
                  'maxPerDay',
                )}
                {field(
                  'Gap (min)',
                  <InputNumber
                    aria-label="Minimum gap minutes"
                    min={0}
                    style={{ width: 100 }}
                    placeholder="None"
                    value={form.minGapMinutes}
                    onChange={(v) => patchForm({ minGapMinutes: v })}
                  />,
                  'minGapMinutes',
                )}
              </Space>

              <Space wrap>
                {field(
                  'Account age ≥ (days)',
                  <InputNumber
                    aria-label="Min account age days"
                    min={0}
                    style={{ width: 130 }}
                    placeholder="Any"
                    value={form.minAccountAgeDays}
                    onChange={(v) => patchForm({ minAccountAgeDays: v })}
                  />,
                  'minAccountAgeDays',
                )}
                {field(
                  'Account age ≤ (days)',
                  <InputNumber
                    aria-label="Max account age days"
                    min={0}
                    style={{ width: 130 }}
                    placeholder="Any"
                    value={form.maxAccountAgeDays}
                    onChange={(v) => patchForm({ maxAccountAgeDays: v })}
                  />,
                  'maxAccountAgeDays',
                )}
              </Space>

              <Space size={6}>
                <Switch
                  aria-label="Campaign enabled"
                  checked={form.enabled}
                  onChange={(v) => patchForm({ enabled: v })}
                />
                <Typography.Text>
                  {form.enabled ? 'Enabled — live once saved' : 'Disabled — saved as a draft'}
                </Typography.Text>
              </Space>

              {problem !== null && form.name.length > 0 && (
                <Typography.Text type="danger">{problem}</Typography.Text>
              )}
              {Object.keys(serverFields).length > 0 && (
                <Alert
                  type="error"
                  showIcon
                  message="The server refused some fields"
                  description={Object.entries(serverFields)
                    .map(([k, v]) => `${k}: ${v}`)
                    .join(' · ')}
                />
              )}
            </Space>
          </Col>
          <Col xs={24} md={11}>
            <Typography.Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 8 }}>
              Preview
            </Typography.Text>
            <PromoPreview form={form} pkg={previewPkg} />
          </Col>
        </Row>
      </Modal>

      <StatsDrawer campaign={statsFor} onClose={() => setStatsFor(null)} />
    </Space>
  );
}

/** Paise string → "₹1,234.00" — always two decimals, "—" for garbage. */
function rupeesTwoDecimals(paise: string): string {
  const n = Number(paise);
  if (!Number.isFinite(n)) return '—';
  return `₹${(n / PAISE_PER_RUPEE).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Raw counters for one campaign — zeros are shown as zeros, nothing is derived. */
function StatsDrawer({
  campaign,
  onClose,
}: {
  campaign: AdminPromoCampaign | null;
  onClose: () => void;
}) {
  const stats = useQuery({
    queryKey: [...LIST_KEY, campaign?.id, 'stats'],
    enabled: campaign !== null,
    queryFn: async () => (await api.get<PromoStats>(`/admin/promos/${campaign?.id}/stats`)).data,
  });
  return (
    <Drawer
      title={campaign ? `Stats — ${campaign.name}` : 'Stats'}
      open={campaign !== null}
      onClose={onClose}
      width={420}
    >
      {stats.isError ? (
        <QueryError error={stats.error} what="campaign stats" onRetry={() => void stats.refetch()} />
      ) : stats.isLoading || !stats.data ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : (
        <Descriptions column={1} size="small" bordered>
          <Descriptions.Item label="Users who saw it">{stats.data.uniqueUsers}</Descriptions.Item>
          <Descriptions.Item label="Impressions">{stats.data.impressions}</Descriptions.Item>
          <Descriptions.Item label="Dismissals">{stats.data.dismissals}</Descriptions.Item>
          <Descriptions.Item label="CTA taps">{stats.data.ctaTaps}</Descriptions.Item>
          <Descriptions.Item label="Acknowledgements">{stats.data.acknowledgements}</Descriptions.Item>
          <Descriptions.Item label="Verified conversions">{stats.data.conversions}</Descriptions.Item>
          <Descriptions.Item label="Users who converted">{stats.data.convertedUsers}</Descriptions.Item>
          <Descriptions.Item label="Bonus coins credited">{stats.data.bonusCoinsCredited}</Descriptions.Item>
          <Descriptions.Item label="Captured recharges">{stats.data.capturedOrders}</Descriptions.Item>
          <Descriptions.Item label="Captured amount">
            {rupeesTwoDecimals(stats.data.capturedAmountPaise)}
          </Descriptions.Item>
        </Descriptions>
      )}
    </Drawer>
  );
}
